import { CalendarEvent, CalendarEventsResponse, MeetingType } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export interface RescheduleParams {
  calendarEventId: string
  startTime: Date
  endTime: Date
}

export async function rescheduleCalendarEvent(context: ApiContext, params: RescheduleParams): Promise<void> {
  await apiContextFetch(context, "calendar_event_reschedules", {
    method: "POST",
    body: JSON.stringify({
      calendar_event_id: params.calendarEventId,
      start_time: params.startTime.toISOString(),
      end_time: params.endTime.toISOString(),
    }),
  })
}

export function fetchCalendarEvents(
  context: ApiContext,
  options: {
    calendarIds?: string[]
    startDate?: Date
    endDate?: Date
  }
): Promise<CalendarEventsResponse> {
  const params = new URLSearchParams()
  if (options.startDate) params.append("start_date", options.startDate.toISOString().split("T")[0])
  if (options.endDate) params.append("end_date", options.endDate.toISOString().split("T")[0])
  options.calendarIds?.forEach((id) => params.append("calendar_ids[]", id))

  const queryString = params.toString() ? `?${params.toString()}` : ""
  return apiContextFetch<CalendarEventsResponse>(context, `calendar_events${queryString}`)
}

export function fetchCalendarEvent(context: ApiContext, id: string): Promise<CalendarEvent> {
  return apiContextFetch<CalendarEvent>(context, `calendar_events/${id}`)
}

export type CalendarEventTypeKey = "timespan" | "date" | "datespan"

export function isAllDayEvent(event: Pick<CalendarEvent, "event_type">): boolean {
  return event.event_type !== "timespan"
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export interface CreateCalendarEventParams {
  calendarId: string
  title: string
  eventType?: CalendarEventTypeKey
  // For eventType "timespan" (default): both required.
  startTime?: Date
  endTime?: Date
  // For eventType "date": single-day all-day.
  date?: Date
  // For eventType "datespan": multi-day all-day (end is inclusive — the API converts to Nylas's exclusive convention).
  startDate?: Date
  endDate?: Date
  descriptionHtml?: string
  location?: string
  participants?: { email: string; name?: string }[]
  addConferencing?: boolean
  colorId?: string
  pendingMeetingType?: MeetingType
  providerCategories?: string[]
}

export async function createCalendarEvent(
  context: ApiContext,
  params: CreateCalendarEventParams
): Promise<CalendarEvent> {
  return apiContextFetch<CalendarEvent>(context, "calendar_events", {
    method: "POST",
    body: JSON.stringify({
      calendar_id: params.calendarId,
      title: params.title,
      event_type: params.eventType,
      start_time: params.startTime?.toISOString(),
      end_time: params.endTime?.toISOString(),
      date: params.date ? toIsoDate(params.date) : undefined,
      start_date: params.startDate ? toIsoDate(params.startDate) : undefined,
      end_date: params.endDate ? toIsoDate(params.endDate) : undefined,
      description_html: params.descriptionHtml,
      location: params.location,
      participants: params.participants,
      add_conferencing: params.addConferencing,
      color_id: params.colorId,
      pending_meeting_type: params.pendingMeetingType,
      provider_categories: params.providerCategories,
    }),
  })
}

export interface UpdateCalendarEventParams {
  title?: string
  eventType?: CalendarEventTypeKey
  startTime?: Date
  endTime?: Date
  date?: Date
  startDate?: Date
  endDate?: Date
  descriptionHtml?: string
  location?: string
  participants?: { email: string; name?: string; status?: string }[]
  providerCategories?: string[]
}

export async function updateCalendarEvent(
  context: ApiContext,
  id: string,
  params: UpdateCalendarEventParams
): Promise<void> {
  await apiContextFetch(context, `calendar_events/${id}`, {
    method: "PUT",
    body: JSON.stringify({
      title: params.title,
      event_type: params.eventType,
      start_time: params.startTime?.toISOString(),
      end_time: params.endTime?.toISOString(),
      date: params.date ? toIsoDate(params.date) : undefined,
      start_date: params.startDate ? toIsoDate(params.startDate) : undefined,
      end_date: params.endDate ? toIsoDate(params.endDate) : undefined,
      description_html: params.descriptionHtml,
      location: params.location,
      participants: params.participants,
      provider_categories: params.providerCategories,
    }),
  })
}

export async function deleteCalendarEvent(context: ApiContext, id: string, destroyOnProvider: boolean): Promise<void> {
  await apiContextFetch(context, `calendar_events/${id}?destroy_on_provider=${destroyOnProvider}`, {
    method: "DELETE",
  })
}

export async function rsvpCalendarEvent(
  context: ApiContext,
  calendarEventId: string,
  status: "yes" | "no" | "maybe"
): Promise<void> {
  await apiContextFetch(context, "calendar_event_rsvps", {
    method: "POST",
    body: JSON.stringify({ calendar_event_id: calendarEventId, status }),
  })
}
